import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocalStorage } from '~/hooks/use-local-storage';
import { ButtonPosition, PositionCoordinates } from './types';

const STORAGE_KEY = 'ai-assistant-button-position';
const DEFAULT_SPACING = '24px'; // theme.spacings[600]

interface UseAIButtonPositionOptions {
  draggable?: boolean;
}

export const useAIButtonPosition = (options: UseAIButtonPositionOptions = {}) => {
  const { draggable = true } = options;
  const [savedPosition, setSavedPosition] = useLocalStorage<ButtonPosition | null>(STORAGE_KEY, null);
  const [currentPosition, setCurrentPosition] = useState<PositionCoordinates>(
    getPositionCoordinates(savedPosition || 'bottom-right')
  );
  const [isDragging, setIsDragging] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const dragOffset = useRef({ x: 0, y: 0 });

  // Convert position name to coordinates
  function getPositionCoordinates(position: ButtonPosition): PositionCoordinates {
    const coords: Record<ButtonPosition, PositionCoordinates> = {
      'bottom-right': { bottom: DEFAULT_SPACING, right: DEFAULT_SPACING },
      'bottom-left': { bottom: DEFAULT_SPACING, left: DEFAULT_SPACING },
      'top-right': { top: DEFAULT_SPACING, right: DEFAULT_SPACING },
      'top-left': { top: DEFAULT_SPACING, left: DEFAULT_SPACING },
    };
    return coords[position];
  }

  // Cycle through positions on double-click
  const cyclePosition = useCallback(() => {
    const positions: ButtonPosition[] = ['bottom-right', 'bottom-left', 'top-left', 'top-right'];
    const current = savedPosition || 'bottom-right';
    const currentIndex = positions.indexOf(current);
    const nextIndex = (currentIndex + 1) % positions.length;
    const nextPosition = positions[nextIndex];

    setSavedPosition(nextPosition);
    setCurrentPosition(getPositionCoordinates(nextPosition));
  }, [savedPosition, setSavedPosition]);

  // Handle drag start
  const handleDragStart = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!draggable) return;

      // Only drag on middle mouse button or Alt+Click
      if (e.button !== 1 && !e.altKey) return;

      e.preventDefault();
      setIsDragging(true);

      const button = buttonRef.current;
      if (!button) return;

      const rect = button.getBoundingClientRect();
      dragStartPos.current = { x: e.clientX, y: e.clientY };
      dragOffset.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    },
    [draggable]
  );

  // Handle dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const buttonSize = 56; // Button width/height

      // Calculate new position
      let newX = e.clientX - dragOffset.current.x;
      let newY = e.clientY - dragOffset.current.y;

      // Constrain to viewport
      newX = Math.max(0, Math.min(newX, viewportWidth - buttonSize));
      newY = Math.max(0, Math.min(newY, viewportHeight - buttonSize));

      // Determine which corner is closest
      const distanceToRight = viewportWidth - (newX + buttonSize);
      const distanceToBottom = viewportHeight - (newY + buttonSize);
      const distanceToLeft = newX;
      const distanceToTop = newY;

      const horizontal = distanceToRight < distanceToLeft ? 'right' : 'left';
      const vertical = distanceToBottom < distanceToTop ? 'bottom' : 'top';

      // Set position coordinates
      const newPosition: PositionCoordinates = {
        [vertical]: vertical === 'bottom' ? `${distanceToBottom}px` : `${distanceToTop}px`,
        [horizontal]: horizontal === 'right' ? `${distanceToRight}px` : `${distanceToLeft}px`,
      };

      setCurrentPosition(newPosition);
    };

    const handleMouseUp = () => {
      setIsDragging(false);

      // Save the new position preference
      const pos = currentPosition;
      if (pos.bottom && pos.right) setSavedPosition('bottom-right');
      else if (pos.bottom && pos.left) setSavedPosition('bottom-left');
      else if (pos.top && pos.right) setSavedPosition('top-right');
      else if (pos.top && pos.left) setSavedPosition('top-left');
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, currentPosition, setSavedPosition]);

  return {
    position: currentPosition,
    isDragging,
    buttonRef,
    cyclePosition,
    handleDragStart,
  };
};
